import { useState, useEffect, useMemo } from 'react';
import { notesApiService } from '../services/notesApi';
import { TicketLink } from '../types/player';
import { useAuth } from '../contexts/AuthContext';
import { Ticket, Plus, Trash2, AlertCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { CreatorProfile } from './CreatorProfile';
import { ConfirmationModal } from './ConfirmationModal';
import { TicketIdLink } from './TicketIdLink';
import { SectionHeader } from './ui/SectionHeader';
import { SkeletonLoader } from './SkeletonLoader';

interface TicketLinksProps {
  itemId: string;
  itemType: 'note' | 'warning' | 'ban';
}

export const TicketLinks = ({ itemId, itemType }: TicketLinksProps) => {
  const [tickets, setTickets] = useState<TicketLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newTicketId, setNewTicketId] = useState('');
  const [isAddingTicket, setIsAddingTicket] = useState(false);
  const [isUnlinkModalOpen, setIsUnlinkModalOpen] = useState(false);
  const [ticketToUnlink, setTicketToUnlink] = useState<number | null>(null);
  const { identity } = useAuth();

  const canManage = useMemo(() => {
    if (!identity) return false;
    const groups = new Set(identity.raw.custom.groups);
    return groups.has('SPY_Notes') || groups.has('SPY_Notes_Editor') || groups.has('SPY_Super_admin');
  }, [identity]);

  const fetchTickets = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await notesApiService.getTicketsForItem(itemId);
      setTickets(data);
    } catch (err) {
      setError('Failed to load tickets.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [itemId]);

  const validateTicketId = (ticketId: string): boolean => {
    if (!ticketId || ticketId.trim() === '') return false;
    const num = parseInt(ticketId, 10);
    if (isNaN(num) || num <= 0) return false;
    if (ticketId.startsWith('0') && ticketId.length > 1) return false;
    return true;
  };

  const handleLinkTicket = async () => {
    if (!validateTicketId(newTicketId)) {
      alert('Invalid ticket ID. Must be a positive number and cannot start with 0.');
      return;
    }

    const ticketId = parseInt(newTicketId, 10);
    setIsAddingTicket(true);
    try {
      await notesApiService.linkTicket(ticketId, itemId, itemType);
      setNewTicketId('');
      fetchTickets();
    } catch (err) {
      alert('Failed to link ticket.');
    } finally {
      setIsAddingTicket(false);
    }
  };

  const handleUnlinkRequest = (ticketId: number) => {
    setTicketToUnlink(ticketId);
    setIsUnlinkModalOpen(true);
  };

  const handleUnlinkConfirm = async () => {
    if (ticketToUnlink === null) return;

    try {
      await notesApiService.unlinkTicket(ticketToUnlink, itemId);
      setTickets(prev => prev.filter(t => t.ticket_id !== ticketToUnlink));
    } catch (err) {
      alert('Failed to unlink ticket.');
    } finally {
      setIsUnlinkModalOpen(false);
      setTicketToUnlink(null);
    }
  };

  return (
    <div className="subcard p-3 md:p-4">
      <SectionHeader
        title="Linked Tickets"
        icon={Ticket}
        count={tickets.length}
        className="mb-2"
        actions={canManage ? (
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={newTicketId}
              onChange={(e) => setNewTicketId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLinkTicket()}
              placeholder="Ticket ID"
              aria-label="Ticket ID"
              className="glass-input min-h-0 h-8 py-1 px-2.5 text-xs w-28 tabular-nums"
            />
            <button
              onClick={handleLinkTicket}
              disabled={isAddingTicket || !newTicketId}
              className="glass-button-sm shrink-0"
            >
              <Plus className="w-3.5 h-3.5" /> Link
            </button>
          </div>
        ) : undefined}
      />

      {loading && (
        <div className="space-y-2 py-1" aria-busy="true">
          {[0, 1].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <SkeletonLoader className="h-7 w-7 rounded-md" />
              <SkeletonLoader className="h-3.5 w-24" />
              <SkeletonLoader className="h-3 w-40" />
            </div>
          ))}
        </div>
      )}
      {error && (
        <div className="alert alert-danger py-2">
          <AlertCircle /> <span>{error}</span>
        </div>
      )}

      {!loading && !error && tickets.length === 0 && (
        <p className="text-xs text-glass-500 py-1">No tickets linked.</p>
      )}

      {!loading && !error && tickets.length > 0 && (
        <ul className="divide-y divide-glass-200/10">
          {tickets.map((ticket) => (
            <li key={ticket.ticket_id} className="flex items-center justify-between gap-3 py-2.5">
              <div className="flex items-center gap-3 min-w-0">
                <span className="icon-tile icon-tile-sm icon-tile-info">
                  <Ticket className="w-3.5 h-3.5" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white leading-tight">
                    <TicketIdLink ticketId={ticket.ticket_id} className="text-white hover:text-accent-300">
                      Ticket #{ticket.ticket_id}
                    </TicketIdLink>
                  </p>
                  <p className="text-xs text-glass-400 mt-0.5 flex flex-wrap items-center gap-x-1">
                    <span className="tabular-nums">{formatDistanceToNow(new Date(ticket.linked_at), { addSuffix: true })}</span>
                    <span className="text-glass-500">·</span>
                    <span>Linked by</span> <CreatorProfile creatorId={ticket.linked_by} inline />
                  </p>
                </div>
              </div>
              {canManage && (
                <button
                  onClick={() => handleUnlinkRequest(ticket.ticket_id)}
                  className="btn-icon btn-icon-sm text-glass-400 hover:text-red-400"
                  title="Unlink Ticket"
                  aria-label="Unlink Ticket"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {isUnlinkModalOpen && ticketToUnlink !== null && (
        <ConfirmationModal
          isOpen={isUnlinkModalOpen}
          onClose={() => setIsUnlinkModalOpen(false)}
          onConfirm={handleUnlinkConfirm}
          title="Unlink Ticket"
          message={`Are you sure you want to unlink ticket #${ticketToUnlink}? This action cannot be undone.`}
        />
      )}
    </div>
  );
};
